<template>
    <div
        ref="primaryMenu"
        class="primary-page-menu w-100 gap-1.5"
    >
        <div class="relative">
            <div
                class="grid gap-[6px] pr-[12px] grid-columns overflow-hidden max-h-[130px] w-100 preview-items"
            >
                <template
                    v-for="(menuItem, index) in previewItems"
                    :key="menuItem.link + '_' + menuItem.label"
                >
                    <primary-page-menu-item
                        :first-level-menu-index="index"
                        :menu-item="menuItem"
                        :is-active="isPrimaryItemActive(menuItem)"
                        :class="{ 'focused-item': focusedIndex === index }"
                        class="preview-first-level"
                        tabindex="0"
                        @primary-item-click="closeMenuModal"
                        @dropdown-item-click="handleDropdownItemClick"
                    />
                </template>
            </div>
            <div
                v-if="showMenuModal"
                class="flex flex-col primary-menu-modal absolute bg-neutral-100 ml-[-12px] mt-[-12px] rounded-md top-0 z-40 preview-items"
                data-test="primary-menu-modal"
            >
                <div class="grid gap-[6px] grid-columns overflow-hidden p-[12px]">
                    <template
                        v-for="(menuItem, index) in allMenuItems"
                        :key="menuItem.link + '_' + menuItem.label + '_' + 'modal'"
                    >
                        <primary-page-menu-item
                            :first-level-menu-index="index"
                            :menu-item="menuItem"
                            :is-in-modal="true"
                            :is-active="isPrimaryItemActive(menuItem)"
                            :class="{ 'focused-item': focusedIndex === index }"
                            class="menu-first-level"
                            tabindex="0"
                            @primary-item-click="closeMenuModal"
                            @dropdown-item-click="handleDropdownItemClick"
                        />
                    </template>
                </div>
                <lite-sidebar
                    v-if="liteSidebarStore.hasLiteSidebar"
                    class="lite-sidebar block md:hidden mt-7 md:mt-0 mx-[12px]"
                />
            </div>
        </div>
        <button
            v-if="allMenuItems && allMenuItems.length > 0"
            id="menu-more"
            ref="moreButton"
            data-test="menu-more"
            class="p-3.5 h-[50px] translate-y-8"
            :class="{
                'bg-white': !showMenuModal,
                'bg-neutral-100': showMenuModal,
                'shadow-big': showMenuModal,
                'z-40': !isHeaderMenuOpen,
                'z-10': isHeaderMenuOpen
            }"
            data-bs-toggle="modal"
            aria-haspopup="true"
            aria-expanded="false"
            @click="primaryMenuButtonClicked"
        >
            <icon-menu-more
                v-if="!showMenuModal"
                class="menu-more-icon icon-small"
            />
            <icon-menu-close
                v-if="showMenuModal"
                class="menu-close-icon icon-small"
            />
        </button>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
    import PrimaryPageMenuItem from '~/components/pageNavigation/PrimaryPageMenuItem.vue';
    import IconMenuMore from '~/components/icons/IconMenuMore.vue';
    import IconMenuClose from '~/components/icons/IconMenuClose.vue';
    import { useNavigationStore } from '~/stores/navigationStore';
    import { useLiteSidebarStore } from '~/stores/liteSidebarStore';
    import { storeToRefs } from 'pinia';
    import { getNavItemsGroupToDisplay } from '~/utils/helpers';
    import LiteSidebar from '~/components/pageFooter/LiteSidebar.vue';
    import { useRoute } from 'vue-router';
    import type { INavItem } from '~/types/types';
    import { useAccountStore } from '~/stores/accountStore';

    defineProps({
        isHeaderMenuOpen: {
            type: Boolean,
            required: false,
            default: false
        }
    });

    const liteSidebarStore = useLiteSidebarStore();
    const emit = defineEmits(['showPrimaryMenuChanged']);

    const navStore = useNavigationStore();
    const reactiveNavStore = storeToRefs(useNavigationStore());
    const allMenuItems = reactiveNavStore.state.value.navItems;
    const activeNavItem = navStore.getActiveNavItemForGivenLevel(1);
    const activeNavItemSlug = ref('');
    const showMenuModal = ref(false);
    const screenWidth = ref(0);
    const focusedIndex = ref(-1);
    const hasSecondLevelHoveredItem = ref(false);
    const moreButton = ref<HTMLButtonElement | null>(null);
    const primaryMenu = ref<HTMLElement | null>(null);
    const { buttonColor } = useAccountStore().state;

    const previewItems = computed(() => {
        return getNavItemsGroupToDisplay(screenWidth.value, allMenuItems, navStore);
    });

    const isPrimaryItemActive = (menuItem: INavItem) => {
        const primaryActiveItem = navStore.getActiveNavItemForGivenLevel(1);
        return menuItem.type !== 'custom' && primaryActiveItem?.slug === menuItem.slug;
    };

    const closeMenuModal = () => {
        showMenuModal.value = false;
        emit('showPrimaryMenuChanged', showMenuModal.value);
    };

    const primaryMenuButtonClicked = () => {
        showMenuModal.value = !showMenuModal.value;
        emit('showPrimaryMenuChanged', showMenuModal.value);
    };

    const handleDropdownItemClick = () => {
        closeMenuModal();
        setActiveNavItemSlug();
    };

    const setScreenWidth = () => {
        screenWidth.value = window.innerWidth;
    };

    const { path } = useRoute();
    const setActiveNavItemSlug = () => {
        let isInNavItems = true;
        const parts = path.split('/');

        if (parts && parts.length > 0) {
            const routeSlug = parts[parts.length - 1];
            isInNavItems = reactiveNavStore.state.value.navItems.some(obj => obj.slug.includes(routeSlug));
        }

        if (!isInNavItems) {
            // in case there is a page that is not in menu items
            return activeNavItemSlug.value = '';
        } else if (activeNavItem) {
            return activeNavItemSlug.value = activeNavItem.slug;
        } else {
            return activeNavItemSlug.value = 'dashboard';
        }
    };

    watch(
        () => reactiveNavStore.state.value.activeNavItems[0],
        newActiveItem => {
            if (newActiveItem?.slug === 'dashboard') {
                activeNavItemSlug.value = 'dashboard';
            }

            setActiveNavItemSlug();
            closeMenuModal();
        },
    );

    watch(
        () => showMenuModal.value,
        newState => {
            if (screen.width >= 760) {
                if (newState) {
                    document.documentElement.classList.add('modal-open');
                    document.body.style.width = '100vw';
                    document.getElementsByClassName('page-wrapper')[0].classList.add('modal-open');
                } else {
                    document.documentElement.classList.remove('modal-open');
                    document.body.style.width = '';
                    document.getElementsByClassName('page-wrapper')[0].classList.remove('modal-open');
                }
            }
        }
    );

    const handleArrowNavigation = (event: any) => {
        // Check for inputs or editable elements
        const target = event.target as HTMLElement;
        const isInputLike =
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable;

        if (isInputLike) {
            return; // Skip menu navigation when focused inside inputs or editable elements
        }

        const items = showMenuModal.value ? allMenuItems : previewItems.value;
        const imageNavItems = primaryMenu.value?.querySelectorAll(showMenuModal.value ? '.menu-first-level' : '.preview-first-level') ?? [];
        const totalItems = imageNavItems.length + 1; // Include the "menu-more" button
        const secondLevelItems = document.querySelectorAll('.dropdown-menu.second-level .dropdown-item');
        const thirdLevelItems = document.querySelectorAll('.dropdown-menu.third-level .dropdown-item');

        if (!items || items.length === 0 || !moreButton.value || !primaryMenu.value) return;

        if ((secondLevelItems.length > 0 && thirdLevelItems.length > 0)
            || (secondLevelItems.length > 0 && thirdLevelItems.length === 0 && hasHoveredItemInSecondLevelMenu())
        ) {
            return;
        }

        switch (event.key) {
        case 'ArrowRight': {
            event.preventDefault();
            focusedIndex.value = (focusedIndex.value + 1) % totalItems;
            if (focusedIndex.value === imageNavItems.length) {
                moreButton.value.focus();
            } else {
                (imageNavItems[focusedIndex.value] as HTMLElement | undefined)?.focus();
            }
            break;
        }
        case 'ArrowLeft': {
            event.preventDefault();
            focusedIndex.value = (focusedIndex.value - 1 + totalItems) % totalItems;
            if (focusedIndex.value === imageNavItems.length) {
                moreButton.value.focus();
            } else {
                (imageNavItems[focusedIndex.value] as HTMLElement | undefined)?.focus();
            }
            break;
        }
        // @ts-ignore
        case 'Enter' && hasHoveredItemInSecondLevelMenu(): {
            event.preventDefault();
            if (focusedIndex.value === imageNavItems.length) {
                primaryMenuButtonClicked();
                focusedIndex.value = showMenuModal.value ? allMenuItems.length : previewItems.value.length;
            } else {
                (imageNavItems[focusedIndex.value] as HTMLElement | undefined)?.focus();
            }
            break;
        }
        default:
            break;
        }
    };

    const hasHoveredItemInSecondLevelMenu = () => {
        const secondLevelItemsHovered = document.querySelectorAll('.dropdown-menu.image-nav-dropdown.second-level.first-level-index-' + focusedIndex.value);
        secondLevelItemsHovered.forEach(item => {
            // Check if any child nodes have the 'hovered' class
            hasSecondLevelHoveredItem.value = Array.from(item.childNodes)
                .filter(child => child.nodeType === Node.ELEMENT_NODE) // Ensure the child is an element
                // @ts-ignore
                .some(child => child.classList.contains('hovered'));
        });
        return hasSecondLevelHoveredItem.value;
    };

    onMounted(() => {
        setActiveNavItemSlug();
        setScreenWidth();

        window.addEventListener('resize', () => setScreenWidth());
        window.addEventListener('keydown', handleArrowNavigation);
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', handleArrowNavigation);
    });
</script>

<style scoped lang="scss">
    $buttonColor: v-bind(buttonColor);

    .primary-page-menu {
        display: grid;
        grid-template-columns: auto 50px;
    }

    #menu-more {
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.07);
        @apply rounded-md;

        &:hover {
            box-shadow: 0 1px 20px 0 rgba(0, 0, 0, .1);
        }
    }

    .primary-menu-modal {
        box-shadow: 0 1px 20px 0 rgba(0, 0, 0, .1);
        backdrop-filter: blur(5px);
        width: calc(100% + 12px);
    }

    .shadow-big {
        box-shadow: 0 1px 20px 0 rgba(0,0,0,.1);
    }

    .grid-columns {
        grid-template-columns: repeat(3, minmax(0, 1fr));

        @media (min-width: 540px) {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        @media (min-width: 768px) {
            grid-template-columns: repeat(5, minmax(0, 1fr));
        }

        @media (min-width: 992px) {
            grid-template-columns: repeat(7, minmax(0, 1fr));
        }
    }

    /* we will explain what these classes do next! */
    .v-enter-active,
    .v-leave-active {
        transition: opacity 0.5s ease;
    }

    .v-enter-from,
    .v-leave-to {
        opacity: 0;
    }

    .preview-items {
        overflow: unset !important;

        .focused-item {
            outline: 2px solid $buttonColor !important;
        }
    }

    #menu-more {
        &:focus-visible {
            outline: 2px solid $buttonColor !important;
        }
    }
</style>
